import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { X, History, ArrowRight, AlertCircle } from "lucide-react";
import {
  getSubscriptionHistory,
  coerceTier,
  coerceStatus,
  type SubscriptionAuditLogView,
  type CompanySubscriptionView,
} from "../../../api/General/Subscription";
import { Spinner } from "../../../components/ui/Spinner";
import { formatDate, relativeTime, tierVisual, initials } from "./helpers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanySubscriptionView | null;
}

/** Map server "action" strings to a translation key + neutral fallback label. */
function actionLabel(
  raw: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const norm = (raw || "").toLowerCase();
  if (norm.includes("extend")) return t("Trial extended");
  if (norm.includes("start") && norm.includes("trial")) return t("Trial started");
  if (norm.includes("mark") && norm.includes("paid")) return t("Marked as paid");
  if (norm.includes("revoke")) return t("Subscription revoked");
  if (norm.includes("tier")) return t("Tier change");
  if (norm.includes("status")) return t("Status change");
  return raw || t("Status change");
}

/** Dot color per action category. */
function actionDotClass(raw: string): string {
  const norm = (raw || "").toLowerCase();
  if (norm.includes("revoke")) return "bg-red-500 ring-red-100 dark:ring-red-950/60";
  if (norm.includes("mark") && norm.includes("paid"))
    return "bg-emerald-500 ring-emerald-100 dark:ring-emerald-950/60";
  if (norm.includes("trial")) return "bg-amber-500 ring-amber-100 dark:ring-amber-950/60";
  return "bg-slate-400 ring-slate-100 dark:ring-slate-800";
}

function TierChip({ tier }: { tier: ReturnType<typeof coerceTier> }) {
  const v = tierVisual(tier);
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${v.pill}`}>
      {tier}
    </span>
  );
}

export default function SubscriptionHistorySheet({ open, onOpenChange, company }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<SubscriptionAuditLogView[]>([]);

  useEffect(() => {
    if (!open || !company) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEntries([]);
    getSubscriptionHistory(company.id)
      .then((res) => {
        if (cancelled) return;
        if (res.errors && res.errors.length > 0) {
          setError(res.errors[0]?.message || t("Failed to load subscriptions"));
        } else {
          setEntries(res.payload || []);
        }
      })
      .catch(() => {
        if (!cancelled) setError(t("Failed to load subscriptions"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, company, t]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
        />
        <DialogPrimitive.Content
          className="fixed top-0 z-50 h-full w-full max-w-md
            ltr:right-0 rtl:left-0
            bg-card border-l border-border ltr:border-l rtl:border-l-0 rtl:border-r shadow-2xl flex flex-col
            data-[state=open]:animate-in data-[state=open]:ltr:slide-in-from-right data-[state=open]:rtl:slide-in-from-left
            data-[state=closed]:animate-out data-[state=closed]:ltr:slide-out-to-right data-[state=closed]:rtl:slide-out-to-left
            data-[state=open]:duration-300 data-[state=closed]:duration-200"
        >
          {/* Header */}
          <div className="p-5 border-b border-border bg-gradient-to-br from-slate-50 to-white dark:from-slate-900/60 dark:to-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {company?.imageUrl ? (
                  <img
                    src={company.imageUrl}
                    alt={company.name}
                    className="w-10 h-10 rounded-lg object-cover ring-1 ring-border"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center text-sm font-semibold">
                    {initials(company?.name)}
                  </div>
                )}
                <div className="min-w-0">
                  <DialogPrimitive.Title className="text-sm font-medium text-muted-foreground inline-flex items-center gap-1.5">
                    <History size={14} />
                    {t("Subscription History")}
                  </DialogPrimitive.Title>
                  <div className="mt-0.5 text-base font-semibold text-foreground truncate">
                    {company?.name}
                  </div>
                </div>
              </div>
              <DialogPrimitive.Close
                className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t("Cancel")}
              >
                <X size={16} />
              </DialogPrimitive.Close>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Spinner size="md" />
              </div>
            )}

            {error && !loading && (
              <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && entries.length === 0 && (
              <div className="text-center py-16 text-sm text-muted-foreground">
                {t("No subscription history yet.")}
              </div>
            )}

            {!loading && !error && entries.length > 0 && (
              <ol className="relative ms-2">
                {/* Timeline rail */}
                <span
                  aria-hidden="true"
                  className="absolute top-1 bottom-1 start-[5px] w-px bg-border"
                />
                {entries.map((entry) => {
                  const fromTier = coerceTier(entry.fromTier);
                  const toTier = coerceTier(entry.toTier);
                  const fromStatus = coerceStatus(entry.fromStatus);
                  const toStatus = coerceStatus(entry.toStatus);
                  const tierChanged = fromTier !== toTier;
                  const statusChanged = fromStatus !== toStatus;
                  const trialChanged =
                    entry.trialEndsAtBefore !== entry.trialEndsAtAfter &&
                    (entry.trialEndsAtBefore || entry.trialEndsAtAfter);
                  const paidChanged =
                    entry.paidUntilBefore !== entry.paidUntilAfter &&
                    (entry.paidUntilBefore || entry.paidUntilAfter);
                  return (
                    <li key={entry.id} className="relative ps-7 pb-6 last:pb-1">
                      {/* Dot */}
                      <span
                        aria-hidden="true"
                        className={`absolute start-0 top-1 w-[11px] h-[11px] rounded-full ring-[3px] ${actionDotClass(entry.action)}`}
                      />
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 className="text-sm font-semibold text-foreground">
                          {actionLabel(entry.action, t)}
                        </h3>
                        <time
                          dateTime={entry.performedAt}
                          title={new Date(entry.performedAt).toLocaleString()}
                          className="shrink-0 text-xs text-muted-foreground tabular-nums"
                        >
                          {relativeTime(entry.performedAt, t)}
                        </time>
                      </div>

                      {/* Tier transition */}
                      {tierChanged && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <TierChip tier={fromTier} />
                          <ArrowRight size={12} className="text-muted-foreground rtl:rotate-180" />
                          <TierChip tier={toTier} />
                        </div>
                      )}

                      {/* Status transition (when no tier change but status did change) */}
                      {!tierChanged && statusChanged && (
                        <div className="mt-1.5 text-xs text-muted-foreground">
                          <span className="font-medium">{t("Status change")}:</span>{" "}
                          <span>{t(fromStatus.replace(/([A-Z])/g, " $1").trim())}</span>
                          <span className="mx-1">→</span>
                          <span className="text-foreground font-medium">
                            {t(toStatus.replace(/([A-Z])/g, " $1").trim())}
                          </span>
                        </div>
                      )}

                      {/* Date fields that moved */}
                      {trialChanged && (
                        <div className="mt-1.5 text-xs text-muted-foreground">
                          <span className="font-medium">{t("Trial Ends")}:</span>{" "}
                          <span className="tabular-nums">{formatDate(entry.trialEndsAtBefore)}</span>
                          <span className="mx-1">→</span>
                          <span className="tabular-nums text-foreground font-medium">
                            {formatDate(entry.trialEndsAtAfter)}
                          </span>
                        </div>
                      )}
                      {paidChanged && (
                        <div className="mt-1.5 text-xs text-muted-foreground">
                          <span className="font-medium">{t("Paid Until")}:</span>{" "}
                          <span className="tabular-nums">{formatDate(entry.paidUntilBefore)}</span>
                          <span className="mx-1">→</span>
                          <span className="tabular-nums text-foreground font-medium">
                            {entry.paidUntilAfter ? formatDate(entry.paidUntilAfter) : t("Indefinite")}
                          </span>
                        </div>
                      )}

                      {/* Notes */}
                      {entry.notes && (
                        <p className="mt-2 text-xs text-foreground bg-muted/40 rounded-md px-2.5 py-1.5 whitespace-pre-wrap break-words">
                          {entry.notes}
                        </p>
                      )}

                      {/* Performer */}
                      <div className="mt-2 text-[11px] text-muted-foreground">
                        {entry.performedByName && (
                          <span>{t("by {{name}}", { name: entry.performedByName })}</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
